import { useCallback, useState } from 'react';

import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import useEventHandler from '@/hooks/useEventHandler';
import { UseOnActionType } from '@/hooks/useEventHandler/hooks/useOnAction';
import { ShareMenuActions } from '@/modules/lyearn-connect/types';
import routes from '@/pages/corporate/routes';
import { UpdateEventStatusTypeEnum, WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

import { RescheduleSessionModalProps } from '../components/RescheduleSessionModal';
import { ShareSessionDialogProps } from '../components/ShareSessionDialog';
import { ActionDialog, DialogActions, EventAction } from '../types';
import useEventMutations from './useEventMutations';

export type EventsManagerStateType = {
  inViewActionDialog: ActionDialog | null;
};

export type EventsManagerComponentType = {
  [ActionDialog.Reschedule]: () => RescheduleSessionModalProps;
  [ActionDialog.Share]: () => ShareSessionDialogProps;
};

function useEventAction({
  eventId,
  eventMutations,
  latestUpcomingEventId,
}: {
  eventId: string;
  latestUpcomingEventId?: string | null;
  eventMutations: ReturnType<typeof useEventMutations>;
}) {
  const [inViewActionDialog, setInViewActionDialog] = useState<ActionDialog | null>(null);
  const { onCopyRoute } = useCopyToClipboard();
  const { onArchive, onDelete } = eventMutations;
  const { isAccessible: isWorkspaceAccessible, showPermissionSnackbar } =
    useWorkspaceAccessControl();

  const eventHandler = useEventHandler<EventsManagerStateType, EventsManagerComponentType>();
  const { onEventEmitter } = eventHandler;
  const viewUrl = routes.AdminEventAttendees(latestUpcomingEventId ?? eventId);

  const onAction = useCallback<
    UseOnActionType<string, EventsManagerStateType, EventsManagerComponentType>
  >(
    (actionType, itemType) => {
      switch (actionType) {
        case ShareMenuActions.LearnerCopyLink:
          onCopyRoute(routes.EventDetails(eventId));
          break;
        case EventAction.Cancel:
          isWorkspaceAccessible(WorkspaceActions.Archive, WorkspaceSubjects.Event)
            ? setInViewActionDialog(ActionDialog.Archive)
            : showPermissionSnackbar('archive', 'session');
          break;

        case EventAction.Delete:
          isWorkspaceAccessible(WorkspaceActions.Remove, WorkspaceSubjects.Event)
            ? setInViewActionDialog(ActionDialog.Delete)
            : showPermissionSnackbar('delete', 'session');
          break;
        default: {
          if (itemType.eventHandler) {
            onEventEmitter(itemType.eventHandler);
          }
        }
      }
    },
    [onCopyRoute, eventId, isWorkspaceAccessible, onEventEmitter, showPermissionSnackbar],
  );

  const onDialogAction = useCallback(
    ({
      dialogAction,
      message,
      updateType,
      eventName,
      onSuccessCb,
    }: {
      dialogAction: DialogActions;
      message?: string;
      eventName?: string;
      onSuccessCb?: () => void;
      updateType?: UpdateEventStatusTypeEnum;
    }) => {
      switch (dialogAction) {
        case DialogActions.Close:
          setInViewActionDialog(null);
          break;
        case DialogActions.Archive:
          const onSuccess = () => {
            onSuccessCb?.();
            setInViewActionDialog(null);
          };
          onArchive({ eventId, message, updateType, onSuccessCb: onSuccess });
          break;
        case DialogActions.Delete:
          const onDeleteSuccess = () => {
            onSuccessCb?.();
            setInViewActionDialog(null);
          };

          onDelete({ eventId, onSuccessCb: onDeleteSuccess, updateType, eventName });
          break;
        default:
          return null;
      }
    },
    [eventId, onArchive, onDelete],
  );

  return {
    state: {
      inViewActionDialog,
    },
    data: {
      url: viewUrl,
    },
    methods: {
      onAction,
      onEventEmitter,
      onDialogAction,
    },
    eventHandler,
  };
}

export default useEventAction;
export type useEventActionType = ReturnType<typeof useEventAction>;
